/**
 * Server settings › Overview: the server's name and icon (Manage Server),
 * as Discord's Server Settings › Overview. The icon goes through the same
 * image pipeline as an avatar (any size: decoded, cropped and shrunk in the
 * browser, then re-encoded by the server) and is sent as a data URI to
 * PATCH /guilds/{id}. Fix round 2026-10-28: there was no way to set the
 * server icon in the app.
 */
import type { ModifyGuildRequest } from '@neosocial/protocol';
import { useRef, useState } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { assetUrl } from '../lib/config';
import { blobToDataUri, imageErrorText, looksLikeImage } from '../lib/image';
import { flagOn } from '../store/config';
import { updateData, useData } from '../store/data';
import { useActiveGuildId } from '../store/selectors';
import { runAction } from '../mod/actions';
import { ImageEditor } from '../ui/ImageEditor';
import { Btn } from '../ui/kit';
import { toast } from '../ui/overlay';
import markUrl from '../assets/mark.svg';
import { UnsavedBar } from '../settings/Layer';
import { useUnsaved } from './unsaved';
import { DefaultNotifyField } from '../notify/DefaultNotifyField';
import { AfkSettings } from '../voice/VoiceExtras';

export function OverviewTab() {
  const { t } = useT();
  const gid = useActiveGuildId();
  const guild = useData((d) => (gid ? d.guilds[gid] : undefined));
  /** The name being typed (undefined: unchanged). */
  const [name, setName] = useState<string | undefined>(undefined);
  /** A new icon as a data URI, null to remove it (undefined: unchanged). */
  const [icon, setIcon] = useState<string | null | undefined>(undefined);
  const [editing, setEditing] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  /** Default notification settings (2026-10-29; undefined: unchanged). */
  const [notify, setNotify] = useState<0 | 1 | undefined>(undefined);
  const file = useRef<HTMLInputElement>(null);
  const nameNow = (name ?? guild?.name ?? '').trim();
  const nameChanged = name !== undefined && nameNow !== guild?.name;
  const notifyChanged = notify !== undefined && notify !== guild?.default_message_notifications;
  const dirty = nameChanged || icon !== undefined || notifyChanged;
  useUnsaved('overview', dirty);
  if (!gid || !guild) return null;
  const nameOk = nameNow.length >= 2 && nameNow.length <= 100;
  const shown = icon === undefined ? assetUrl(guild.icon) : icon;
  const reset = () => {
    setName(undefined);
    setIcon(undefined);
    setNotify(undefined);
  };
  const save = async () => {
    if (!dirty || !nameOk || busy) return;
    const body: ModifyGuildRequest = {};
    if (nameChanged) body.name = nameNow;
    if (icon !== undefined) body.icon = icon;
    if (notifyChanged) body.default_message_notifications = notify;
    setBusy(true);
    const r = await runAction(() => api.guilds.modify(gid, body));
    setBusy(false);
    if (!r.ok) return;
    // the gateway's GUILD_UPDATE says the same; this one is at once
    updateData((d) => ({
      ...d,
      guilds: { ...d.guilds, [gid]: { ...d.guilds[gid]!, ...r.value } },
    }));
    reset();
    toast(t('ns.saved'), { icon: 'check', tone: 'ok' });
  };
  return (
    <div className="ns-tool ns-overview" data-overview="">
      <div className="ns-ov-row">
        <span className="ns-ov-icon" data-has-icon={shown ? '' : undefined}>
          <img src={shown ?? markUrl} alt={t('ns.ov.icon')} />
        </span>
        <div className="ns-ov-t">
          <p className="ns-muted">{t('ns.ov.iconHint')}</p>
          <div className="ns-row-btns">
            <Btn
              size="sm"
              icon="upload"
              onClick={() =>
                // the console's Image uploads covers server icons too (the server refuses one then)
                flagOn('image_uploads')
                  ? file.current?.click()
                  : toast(t('cfg.off.image_uploads'), { icon: 'image', tone: 'warn' })
              }
            >
              {t('ns.ov.upload')}
            </Btn>
            {shown ? (
              <Btn size="sm" kind="quiet" onClick={() => setIcon(guild.icon ? null : undefined)}>
                {t('ns.ov.remove')}
              </Btn>
            ) : null}
          </div>
          <input
            ref={file}
            type="file"
            accept="image/*"
            hidden
            data-overview-file=""
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              if (!looksLikeImage(f)) {
                toast(t('img.err.unsupported'), { tone: 'warn', icon: 'image' });
                return;
              }
              setEditing(f);
            }}
          />
        </div>
      </div>
      <label className="ns-field">
        <span className="ns-label">{t('ns.ov.name')}</span>
        <input
          className="nx-input"
          dir="auto"
          value={name ?? guild.name}
          maxLength={100}
          aria-invalid={!nameOk || undefined}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      {!nameOk ? <p className="ns-err">{t('ns.ov.nameLength')}</p> : null}
      <DefaultNotifyField
        value={notify ?? guild.default_message_notifications}
        onChange={setNotify}
      />
      {/* Discord's inactive channel and timeout (2026-11-01): saved at once */}
      <AfkSettings guild={guild} />
      {dirty ? (
        <UnsavedBar onReset={reset} busy={busy} saveDisabled={!nameOk} onSave={() => void save()} />
      ) : null}
      {editing ? (
        <ImageEditor
          file={editing}
          purpose="icon"
          onCancel={() => setEditing(null)}
          onDone={(img) => {
            setEditing(null);
            blobToDataUri(img.blob).then(setIcon, (e: unknown) =>
              toast(imageErrorText(t, e), { tone: 'warn', icon: 'image' }),
            );
          }}
        />
      ) : null}
    </div>
  );
}
